import React, { useMemo, useState } from 'react';
import {
    Collapse, ActionIcon, Table, Text,
    Button,
} from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import ModalAddFee from './add-fee-modal';
import { RefinedSubTrade } from 'app/models/traded-facilities-params';
import { useDispatch } from 'react-redux';
import { deleteSubTradeFees } from 'app/state/ducks/sub-trades/thunks';
import { loadFMMetadataForSubTrade } from 'app/state/ducks/signable-document/thunks';
import DeleteConfirmation from 'app/views/components/delete-confirmation/DeleteConfirmation';
import { SubTradeFeeParams } from 'app/models/sub-trade-params';
import { computeFeeTotals, formatPriceWithoutCurrency } from 'app/utils/util-functions';
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification';
import { paidToOptions } from 'app/models/dropdown-options';
import { SignableDocument } from 'app/models/document-params';
import { FMDocumentMetadata } from 'app/models/fm-metadata-params';

interface DetailPanelProps {
    refinedSubTrade: RefinedSubTrade;
    fmSignableDoc: SignableDocument | null;
    fmMetadata?: FMDocumentMetadata;
    expended: boolean;
    setExpended: (expanded: boolean) => void;
}

export function SubTradeFees({ refinedSubTrade, fmSignableDoc, fmMetadata, expended, setExpended }: DetailPanelProps) {
    const dispatch = useDispatch();
    const [subTradeFee, setSubTradeFee] = useState<SubTradeFeeParams | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteSubFee, setDeletedSubFee] = useState<string | null>(null);

    const isEditable =
        refinedSubTrade.status !== 'Canceled' &&
        (fmSignableDoc === null || fmSignableDoc.signatures?.length === 0) &&
        !fmMetadata?.lockedStatus;

    // Parent already normalises fmMetadata.subTradeFees onto refinedSubTrade.fees,
    // so we read straight from props.
    const subTradeFeesTable = refinedSubTrade?.fees ?? []

    const handleDelete = async (feeType: string) => {
        const deleteData = {
            feeType,
            id: refinedSubTrade.id,
            status: refinedSubTrade.status,
        };

        try {
            const response: any = await dispatch(deleteSubTradeFees(deleteData));
            if (response?.success) {
                SuccessNotification({ title: 'Deleted Fee' });
                if (refinedSubTrade.id) {
                    dispatch(loadFMMetadataForSubTrade(refinedSubTrade.id) as any)
                }
            } else {
                ErrorNotification({
                    title: 'Delete Fee Failed',
                    message: response.payload ?? 'Check data and try again',
                });
            }
        } catch (error) {
            console.error('Error deleting fee:', error);
            ErrorNotification({
                title: 'Delete Fee Failed',
                message: 'Check data and try again',
            });
        } finally {
            setShowDeleteModal(false);
        }
    };

    // Memoize fee totals computation
    const feeTotals = useMemo(
        () => computeFeeTotals(subTradeFeesTable),
        [subTradeFeesTable]
    );

    // Memoize fee totals string
    const feeTotalsString = useMemo(() => {
        return Object.entries(feeTotals)
            .sort((a, b) => a[0].localeCompare(b[0])) // Sort by currency (alphabetically)
            .map(([currency, total]) => `${formatPriceWithoutCurrency(Number(total), 2, 2)} ${currency}`)
            .join(' | ');
    }, [feeTotals]);


    return (
        <div className="panel-box-facilities">
            <div className={`panel-header ${expended ? 'panel-header-open' : ''}`} onClick={() => setExpended(!expended)}>
                <ActionIcon variant="transparent">
                    {expended ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
                </ActionIcon>

                <div className="panel-header-content">
                    <Text className="panel-box-title">Sub Trade Fees</Text>
                    <Text className="panel-box-title">{feeTotalsString}</Text>
                </div>
            </div>
            <Collapse in={expended} style={{ width: '100%' }}>
                <div className="table-container">
                    <Table highlightOnHover striped={false} className="remove-stripe-table sub-trade-fees-table" style={{ width: '100% !important' }}>
                        <thead style={{ backgroundColor: '#F0EEEE' }}>
                            <tr className="payment-summury-overview-table-header">
                                <th className='text-header'>Type</th>
                                <th className='dropdown-header'>Currency</th>
                                <th className='amount-header'>Amount</th>
                                <th className='dropdown-header'>Paid To</th>
                                <th className='text-header'>Notes</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody className="table-body">
                            {subTradeFeesTable.map((e, index) => (
                                <tr key={`${e.feeType}-${index}`} className="table-row" onClick={() => { if (isEditable) { setSubTradeFee(e); setModalOpen(true); } }}>
                                    <td className="payment-summury-overview-table-cell" style={{ textAlign: 'start' }}>{e.feeType ?? ''}</td>
                                    <td className="payment-summury-overview-table-cell" style={{ textAlign: 'center' }}>{e.currency ?? ''}</td>
                                    <td className="payment-summury-overview-table-cell" style={{ textAlign: 'end' }} >{e.paidTo === 'PaidToBuyer' ? `(${formatPriceWithoutCurrency(Number(e.feeAmount), 2, 2) ?? ''})` : formatPriceWithoutCurrency(Number(e.feeAmount), 2, 2) ?? ''}</td>
                                    <td className="payment-summury-overview-table-cell" style={{ textAlign: 'center' }}>{
                                        paidToOptions.find(option => option.value === e.paidTo)?.label ||
                                        (e.paidTo ?? '')
                                    }</td>
                                    <td className="payment-summury-overview-table-cell" style={{ textAlign: 'start' }}>{e.notes ?? ''}</td>
                                    <td className="payment-summury-overview-table-cell">
                                        {isEditable ? <span
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setDeletedSubFee(e.feeType)
                                                setShowDeleteModal(true)
                                            }}
                                            style={{ cursor: 'pointer', color: 'red' }}
                                        >
                                            Delete
                                        </span>
                                            : <></>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
                {isEditable && (
                    <>
                        <div className="action-button-add-fee-div">
                            <Button
                                className="action-button-add-fee-button"
                                disabled={!isEditable}
                                onClick={() => {
                                    setSubTradeFee(null);
                                    setModalOpen(true);
                                }}
                            >
                                Add Fee
                            </Button>
                        </div>
                        <ModalAddFee
                            fee={subTradeFee}
                            opened={modalOpen}
                            onClose={() => setModalOpen(false)}
                            subTrade={refinedSubTrade}
                            onSaved={() => {
                                if (refinedSubTrade.id) {
                                    dispatch(loadFMMetadataForSubTrade(refinedSubTrade.id) as any)
                                }
                            }}
                        />
                        {showDeleteModal && (
                            <DeleteConfirmation
                                onConfirm={() => handleDelete(deleteSubFee ?? '')}
                                onCancel={() => setShowDeleteModal(false)}
                            />
                        )}
                    </>)}
            </Collapse>
        </div>
    );
};

export default SubTradeFees;